import React, { useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAsyncValue, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import EditingActionForm from '../../../../../components/forms/ActionForms/ActionEdition';

// Constants
import { formActionValidationNaming } from '../../../../../components/forms/ActionForms/constants';

// Types
import { type ActionEditionFormValues } from '../../../../../components/forms/ActionForms/ActionEdition/types';
import { type WorkOrder } from '../../../../../types/workOrder';
import { OrderActionReferences } from '../types';
import { Role } from '../../../../../types/auth';

// Redux
import { roidOTSelected } from '../../../../../state/actions/ui/roidOTSelectedGeneralView';

// Hooks
import { useAuth } from '../../../../../hooks/useAuth';

// Utils
import { roleCanViewBudgets } from '../../../../../utils/rolePermissions';
import { roleCanEditBasicSchedule } from '../../../../../components/forms/ActionForms/ActionEdition/basicSchedule';

export default function EditionValidationFormPage(): JSX.Element {
  const { t } = useTranslation('validation');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loaderData = useAsyncValue() as ActionEditionFormValues &
    WorkOrder &
    OrderActionReferences & {
      parentExpeditionOrderStatus?: string;
      isBasicAction?: boolean;
      isBasicLinkedToSegment?: boolean;
    };
  const { user } = useAuth();
  const currentRole = user?.user?.rolActive as Role | undefined;

  // RoomTeam can only view, cannot edit/validate actions
  const isReadOnly = currentRole === Role.RoomTeam;
  // Horario de la básica: lo corrige gestión al validar; la tablet y Sala
  // solo lo ven (plan 2026-10-08). La misma regla usa la validación central.
  const canEditBasicSchedule = roleCanEditBasicSchedule(currentRole, 'validation');

  const onFormSubmit = () => {
    // RoomTeam cannot submit/validate - this is a safety check
    if (isReadOnly) {
      return;
    }
    // Tablets y roles ciegos a budgets (capataz) saltan budget-items directo a materials
    if (currentRole === Role.Tablet || !roleCanViewBudgets(currentRole)) {
      navigate('../materials');
    } else {
      navigate('../budget-items');
    }
  };

  useLayoutEffect(() => {
    dispatch(
      roidOTSelected('actions-validation', loaderData.roid, loaderData.orderId)
    );
  }, []);

  return (
    <EditingActionForm
      onFormSubmit={isReadOnly ? () => {} : onFormSubmit}
      initialData={loaderData}
      namingPersistForm={formActionValidationNaming.NEW_VALIDATION_EDITION_FORM}
      namingUpdateForm={formActionValidationNaming.DATA_CHANGES}
      basicSchedule={
        loaderData.isBasicAction
          ? {
              editable: canEditBasicSchedule,
              // «Recalcular con el recorrido» (12-oct): solo gestión y solo si
              // la básica tiene tramo (sin hueco no hay de dónde recalcular).
              recalculateOrderId:
                canEditBasicSchedule && loaderData.isBasicLinkedToSegment
                  ? loaderData.orderId
                  : undefined,
            }
          : undefined
      }
      textButton={isReadOnly ? t('viewOnly') : currentRole === Role.Tablet ? t('buttons:continue') : t('validate')}
    />
  );
}
